import { type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import type { AppointmentListItem } from '@/hooks/useAppointments';
import type { CalendarAppointmentMeta } from '@/hooks/useCalendarAppointmentMeta';
import { PaymentChip } from '@/components/ui/Chip';
import {
  appointmentJournalRailColor,
  appointmentJournalRailKind,
  calendarBlockLineVisibility,
  getCalendarLifecycleStatus,
} from '@/utils/appointmentCalendarStyle';
import {
  calendarJournalIsCompleted,
  getCalendarBillingPresentation,
  type CalendarBillingPresentation,
} from '@/utils/appointmentCalendarMeta';
import { calendarBlockTitle } from '@/utils/calendarTimeBlock';
import { CLINIC_TIMEZONE } from '@/utils/calendarClinicTime';
import { formatAppointmentTimeRange } from '@/utils/appointmentTimeFormat';
import { cn } from '@/utils/cn';
import { TYPE, TYPE_TABULAR } from '@/lib/typography';

interface AppointmentCalendarCardProps {
  appointment: AppointmentListItem;
  meta?: CalendarAppointmentMeta;
  language: string;
  height: number;
  isConflict?: boolean;
  isDragging?: boolean;
  fillParent?: boolean;
  className?: string;
  style?: React.CSSProperties;
  setNodeRef?: (node: HTMLElement | null) => void;
  dragHandleProps?: Record<string, unknown>;
  onOpenAppointment: () => void;
  showClinician?: boolean;
  children?: ReactNode;
}

function billingHoverLabel(
  t: (key: string, options?: Record<string, string>) => string,
  billing: CalendarBillingPresentation,
): string {
  const method = billing.method
    ? t(`appointments.card.billing_method.${billing.method}`, {
        defaultValue: billing.method.replaceAll('_', ' '),
      })
    : '';
  const status = t(`appointments.card.billing_status.${billing.status}`, {
    defaultValue: billing.status.replaceAll('_', ' '),
  });
  return t(`appointments.card.${billing.labelKey}`, { method, status });
}

function statusLine(
  t: (key: string) => string,
  statusKey: ReturnType<typeof getCalendarLifecycleStatus>,
  serviceName: string | null,
): string | null {
  switch (statusKey) {
    case 'cancelled':
      return t('appointments.legend.cancelled');
    case 'no_show':
      return t('appointments.legend.no_show');
    case 'under_treatment':
      return t('appointments.visit_status.under_treatment');
    case 'checked_in':
    case 'treatment_finished':
    case 'completed':
    case 'upcoming':
      return serviceName;
    default: {
      const _exhaustive: never = statusKey;
      return _exhaustive;
    }
  }
}

export function AppointmentCalendarCard({
  appointment,
  meta,
  language,
  height,
  isConflict,
  isDragging,
  fillParent,
  className,
  style,
  setNodeRef,
  dragHandleProps,
  onOpenAppointment,
  showClinician = false,
  children,
}: AppointmentCalendarCardProps) {
  const { t } = useTranslation();
  const timeBlock = Boolean(appointment.is_blocker);
  const blockTitle = calendarBlockTitle(appointment);
  const billing = meta?.billing ?? getCalendarBillingPresentation(appointment);
  const { showTime, showService } = calendarBlockLineVisibility(height);
  const start = new Date(appointment.starts_at);
  const end = new Date(appointment.ends_at);
  const timeLabel = formatAppointmentTimeRange(start, end, language, CLINIC_TIMEZONE);
  const billingLabel = billingHoverLabel(t, billing);
  const isJournalFinalized = calendarJournalIsCompleted(meta?.journal);
  const railKind = appointmentJournalRailKind({
    status: appointment.status,
    journalFinalized: isJournalFinalized,
  });
  const railColor = appointmentJournalRailColor(railKind);
  const compact = height < 34;
  const cancelled = appointment.status === 'cancelled';
  const noShow = appointment.status === 'no_show';
  const lifecycle = getCalendarLifecycleStatus(appointment);
  const extra = statusLine(t, lifecycle, appointment.service_name);
  const hoverTitle = timeBlock
    ? blockTitle
    : showClinician && appointment.clinician_name
      ? `${appointment.patient_name} · ${appointment.clinician_name}`
      : appointment.patient_name;

  function handleCardKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenAppointment();
    }
  }

  return (
    <div
      ref={setNodeRef}
      {...dragHandleProps}
      role="button"
      tabIndex={0}
      onClick={onOpenAppointment}
      onKeyDown={handleCardKey}
      data-testid="calendar-appointment"
      data-calendar-block={timeBlock ? 'true' : undefined}
      data-appointment-id={appointment.id}
      data-rail={railKind}
      title={hoverTitle}
      style={{
        ...style,
        ...(timeBlock
          ? {
              backgroundImage:
                'repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(15, 23, 42, 0.08) 5px, rgba(15, 23, 42, 0.08) 7px)',
            }
          : {}),
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 50 : style?.zIndex,
      }}
      className={cn(
        'overflow-hidden rounded-[8px] border text-left shadow-card',
        timeBlock
          ? 'border-ds-line-strong bg-ds-sunk text-ds-text'
          : cancelled
            ? 'border-dashed border-ds-line-strong bg-ds-sunk'
            : noShow
              ? 'border-ds-coral-line bg-ds-coral-tint'
              : 'border-ds-line-strong bg-ds-card',
        fillParent ? 'relative h-full w-full' : 'absolute',
        isConflict ? 'animate-pulse ring-4 ring-ds-red ring-offset-1 ring-offset-ds-card' : '',
        className,
      )}
    >
      <span
        className={cn(
          'pointer-events-none absolute bottom-0 left-0 top-0 w-1',
          cancelled && 'opacity-80',
        )}
        style={{
          backgroundColor: timeBlock ? (appointment.service_color ?? '#9CA3AF') : railColor,
        }}
        role={isJournalFinalized && !cancelled ? 'img' : undefined}
        aria-label={
          isJournalFinalized && !cancelled ? t('appointments.card.journal_complete') : undefined
        }
        aria-hidden={!isJournalFinalized || cancelled}
      />
      <div
        className={cn(
          'relative min-w-0 pl-3',
          compact ? 'px-s1 pb-s1 pt-0.5' : 'pb-s1 pr-s2 pt-s1',
        )}
      >
        {showService && billing.status !== 'none' && !timeBlock ? (
          <span
            className="pointer-events-none absolute right-0.5 top-0.5"
            aria-label={billingLabel}
          >
            <PaymentChip
              className="h-5 px-1.5 text-[11px]"
              icon={<Receipt className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden />}
            >
              <span className="sr-only">{billingLabel}</span>
            </PaymentChip>
          </span>
        ) : (
          <span className="sr-only" aria-label={billingLabel}>
            {billingLabel}
          </span>
        )}

        <div
          className={cn('pointer-events-auto min-w-0 select-none', showService && 'pr-6')}
          title={hoverTitle}
        >
          <div
            data-testid="calendar-appointment-patient"
            title={hoverTitle}
            className={cn(
              TYPE.meta,
              'pointer-events-auto break-words font-semibold text-ds-text',
              compact ? 'truncate leading-none' : 'line-clamp-2 leading-tight',
              cancelled && 'text-ds-muted line-through',
            )}
          >
            {timeBlock ? blockTitle : appointment.patient_name}
          </div>
          {showTime && (
            <div className={cn(TYPE.caption, TYPE_TABULAR, 'mt-0.5 truncate text-ds-muted')}>
              <span className="font-semibold">{timeLabel}</span>
              {showService && extra && !timeBlock ? ` · ${extra}` : null}
              {appointment.course_badge
                ? ` · ${t('appointments.course_badge', {
                    n: appointment.course_badge.index,
                    total: appointment.course_badge.total,
                  })}`
                : null}
            </div>
          )}
          {!showTime && appointment.course_badge ? (
            <div className={cn(TYPE.caption, 'mt-0.5 truncate text-ds-muted')}>
              {t('appointments.course_badge', {
                n: appointment.course_badge.index,
                total: appointment.course_badge.total,
              })}
            </div>
          ) : null}
          {showClinician && height >= 40 && appointment.clinician_name && (
            <div
              className={cn(TYPE.caption, 'mt-0.5 truncate font-medium text-ds-muted')}
              data-testid="calendar-card-clinician"
            >
              {appointment.clinician_name}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
